import { interval } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';

import {
    checkout,
    CheckoutResponse,
    checkPendingOrder,
    getPurchaseInfo,
    IPurchaseProductsDto,
    PurchaseInfoProductTypeDto,
} from '@pressreader/commerce-api/legacy';
import { ITrialEligibility, NotEligibleReason } from '@pressreader/commerce-types';
import { localizedString } from '@pressreader/resources';
import { httpGet } from '@pressreader/services';

import ndAlert from 'nd.alert';

function awaitPendingOrder(orderId: string): Promise<CheckoutResponse> {
    return new Promise(resolve => {
        // start polling
        const pollingIntervalMilliseconds = 1000;
        interval(pollingIntervalMilliseconds)
            .pipe(
                switchMap(() => checkPendingOrder(orderId)),
                filter(({ Status }) => {
                    return Status !== 'Pending';
                }),
                take(1),
            )
            .subscribe(resolve);
    });
}

// legacy API do not use for new features
async function checkoutWithConfirmation(requestedContent): Promise<CheckoutResponse> {
    let checkoutResponse = await checkout(requestedContent);

    checkoutResponse = await confirmCheckout(checkoutResponse);

    if (checkoutResponse.Status === 'Declined') {
        throw new Error(localizedString('Checkout.DeclinedMessage'));
    } else if (checkoutResponse.Status !== 'Approved') {
        throw new Error(checkoutResponse.Status);
    }
    return checkoutResponse;
}

async function confirmCheckout(checkoutResponse: CheckoutResponse): Promise<CheckoutResponse> {
    if (checkoutResponse.Status !== 'Pending') {
        return checkoutResponse;
    }

    let wnd = window.open(checkoutResponse.ChallengeUrl);
    if (!wnd) {
        await ndAlert().confirm(
            localizedString('v7.Client.Dialogs.ConfirmCreditCard'),
            localizedString('Buttons.Cancel.Text'),
            localizedString('Buttons.Confirm'),
        );
        wnd = window.open(checkoutResponse.ChallengeUrl);
    }

    const response = await awaitPendingOrder(checkoutResponse.OrderId);
    wnd?.close();

    return response;
}

function getUserPromoEligibility(requestedContent?: any): Promise<ITrialEligibility> {
    return httpGet('subscriptions', 'trial/eligibility', requestedContent).then(
        (result: ITrialEligibility) => result,
        () => ({ isEligible: false, notEligibleReason: NotEligibleReason.None, promoCode: '' }),
    );
}

async function getPurchaseProducts({ issueId }): Promise<IPurchaseProductsDto> {
    const requestedContent: { issueId: string; promoCode?: string } = { issueId };
    const userPromoEligibility = await getUserPromoEligibility();
    if (userPromoEligibility.isEligible) {
        requestedContent.promoCode = userPromoEligibility.promoCode;
    }
    const purchaseInfo = await getPurchaseInfo(requestedContent);

    const singleIssueProduct = purchaseInfo.Products?.find(p => p.Type === PurchaseInfoProductTypeDto.Issue);
    const premiumProduct = purchaseInfo.Products?.find(p => p.Type === PurchaseInfoProductTypeDto.Subscription);
    const selectProduct = purchaseInfo.AllProducts?.find(
        p => p.Type === PurchaseInfoProductTypeDto.Subscription && !p.IsUnlimitedIssues && p.NumberOfIssues > 0,
    );
    const singleTitleProduct = purchaseInfo.Bundles && purchaseInfo.Bundles[0];

    return { singleIssueProduct, premiumProduct, selectProduct, singleTitleProduct };
}

export default {
    checkoutWithConfirmation,
    getUserPromoEligibility,
    getPurchaseProducts,
};
